// ════════════════════════════════════════════════════════════════════════════
// CandidateSelectionScreen — same directory as CandidateManagement, but
// scoped by default to candidates ready for the next step (shortlisted +
// interviewed). The recruiter uses this view to decide who advances to
// offer/hire vs who gets dropped.
//
// This is a thin re-render of CandidateManagementScreen with different
// defaults so the two screens stay in lockstep when we add features.
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView,
  RefreshControl, StatusBar, TextInput, ActivityIndicator, Image, Alert,
  Dimensions,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Same inlined apiFetch as the Management screen — keep both screens
// independent of the (unreliable) utils/apiClient import.
const apiFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = await AsyncStorage.getItem('auth_token').catch(() => null);
  console.log(`[CandidateSel] → ${options.method || 'GET'} ${url}`);
  let res;
  try {
    res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
  } catch (e) {
    console.warn(`[CandidateSel] ← NETWORK FAIL ${url}:`, e?.message);
    throw new Error(`Network: ${e?.message || 'unreachable'}`);
  }
  console.log(`[CandidateSel] ← ${res.status} ${url}`);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json();
};

const normalise = (row) => ({
  id: String(row.id),
  applicationId: row.id,
  name:    row.candidate_name || 'Applicant',
  email:   row.candidate_email || '',
  phone:   row.candidate_phone || '',
  avatar:  row.candidate_avatar || null,
  location: row.candidate_location || '',
  currentTitle: row.current_title || '',
  currentCompany: row.current_company || '',
  jobId:    row.job_id,
  jobTitle: row.job_title || '',
  stage:    (row.stage || 'new').toLowerCase(),
  status:   row.status || 'submitted',
  appliedAt: row.created_at || row.applied_date || null,
  matchScore: row.match_score ?? null,
  interviewsScheduled: Number(row.interviews_scheduled || 0),
  nextInterviewAt: row.next_interview_at || null,
  raw: row,
});

// The "selection" stages — candidates we're actively evaluating. Excludes
// new/reviewed (too early) and rejected/hired (already decided).
const SELECTION_STAGES = ['shortlisted', 'screening', 'interviewed', 'offered'];

// Sub-tabs scoped to selection-relevant stages only.
const TABS = [
  { key: 'all',         label: 'All Selected', color: PRIMARY },
  { key: 'shortlisted', label: 'Shortlisted',  color: '#8B5CF6' },
  { key: 'screening',   label: 'Screening',    color: '#0EA5E9' },
  { key: 'interviewed', label: 'Interviewed',  color: '#F59E0B' },
  { key: 'offered',     label: 'Offered',      color: '#10B981' },
];

export default function CandidateSelectionScreen({ navigation, route }) {
  const [isLoading, setIsLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [activeTab, setActiveTab]   = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // ── Load pipeline ──────────────────────────────────────────────────────
  // Same endpoint as CandidateManagement but we filter client-side to the
  // selection stages. The server could filter via &stage=X but pulling the
  // whole set lets the recruiter switch sub-tabs without re-fetching.
  const loadData = useCallback(async () => {
    try {
      const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
      console.log(`[CandidateSel] company_id from storage: ${cid}`);
      if (!cid) {
        setCandidates([]);
        Alert.alert('No company', 'Please sign in again — company id missing.');
        return;
      }
      const rows = await apiFetch(`/api/recruitment/pipeline?company_id=${cid}&limit=500`);
      const list = Array.isArray(rows) ? rows : (Array.isArray(rows?.data) ? rows.data : []);
      const normalised = list
        .map(normalise)
        .filter(c => SELECTION_STAGES.includes(c.stage));
      setCandidates(normalised);
      console.log(`[CandidateSel] loaded ${normalised.length} selection-stage candidates`);
      await AsyncStorage.setItem('selection_cache', JSON.stringify(normalised)).catch(() => {});

      // Company profile for the header
      try {
        const prof = await apiFetch(`/api/company-profile/${cid}`);
        const p = prof?.data || prof;
        if (p && (p.company_name || p.name)) setCompanyProfile(p);
      } catch (_) {
        const cached = await AsyncStorage.getItem('company_profile').catch(() => null);
        if (cached) { try { setCompanyProfile(JSON.parse(cached)); } catch {} }
      }
    } catch (e) {
      console.warn('[CandidateSel] loadData:', e?.message);
      try {
        const cached = await AsyncStorage.getItem('selection_cache');
        if (cached) setCandidates(JSON.parse(cached));
        else setCandidates([]);
      } catch { setCandidates([]); }
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  const onRefresh = () => { setRefreshing(true); loadData(); };

  // ── Derived ─────────────────────────────────────────────────────────────
  const tabCounts = candidates.reduce((acc, c) => {
    acc.all = (acc.all || 0) + 1;
    acc[c.stage] = (acc[c.stage] || 0) + 1;
    return acc;
  }, {});

  const filtered = candidates.filter(c => {
    if (activeTab !== 'all' && c.stage !== activeTab) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      String(c.name ?? '').toLowerCase().includes(q) ||
      String(c.email ?? '').toLowerCase().includes(q) ||
      String(c.jobTitle ?? '').toLowerCase().includes(q)
    );
  });

  // ── Decision actions — these are what make Selection different from
  // Management. Each writes the stage change to the DB so both sides see it.
  // ────────────────────────────────────────────────────────────────────────
  const moveStage = async (c, newStage, label) => {
    try {
      await apiFetch(`/api/recruitment/applicant/${c.id}/stage`, {
        method: 'POST',
        body: JSON.stringify({ stage: newStage, note: `Moved to ${label} from Selection screen` }),
      });
      // Optimistic local update
      setCandidates(prev =>
        prev.map(x => x.id === c.id ? { ...x, stage: newStage } : x)
            .filter(x => SELECTION_STAGES.includes(x.stage))
      );
      Alert.alert('Moved', `${c.name} moved to ${label}.`);
    } catch (e) {
      console.warn('moveStage:', e?.message);
      Alert.alert('Could not update', e?.message || 'Please try again.');
    }
  };

  const advance = (c) => {
    // Show a stage chooser
    const next = {
      shortlisted: ['screening', 'Move to screening'],
      screening:   ['interviewed', 'Mark as interviewed'],
      interviewed: ['offered',   'Send to offer'],
      offered:     ['hired',     'Mark as hired'],
    }[c.stage];
    if (!next) return;
    moveStage(c, next[0], next[1].split(' ').slice(2).join(' '));
  };

  const reject = (c) => {
    Alert.alert(
      'Reject candidate',
      `Move ${c.name} to rejected? They'll be removed from this list.`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Reject', style: 'destructive', onPress: () => moveStage(c, 'rejected', 'rejected') },
      ]
    );
  };

  // ── Navigation ──────────────────────────────────────────────────────────
  const openDetails = (c) =>
    navigation.navigate('ApplicantDetailsScreen', { applicantId: c.id, applicant: c.raw });
  const openSchedule = (c) =>
    navigation.navigate('ScheduleInterviewScreen', { applicantId: c.id, applicant: c.raw });
  const openOffer = (c) =>
    navigation.navigate('MakeOfferScreen', { applicantId: c.id, applicant: c.raw });

  // ── Render ──────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <SafeAreaView style={st.loading}>
        <StatusBar barStyle="dark-content" />
        <ActivityIndicator size="large" color={PRIMARY} />
        <Text style={st.loadingTxt}>Loading candidates...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={st.container}>
      <StatusBar barStyle="light-content" />

      {/* Header */}
      <LinearGradient colors={['#059669', '#10B981']} style={st.header}>
        <View style={st.headerRow}>
          <TouchableOpacity onPress={() => navigation?.goBack()} style={st.hBtn}>
            <MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" />
          </TouchableOpacity>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={st.hTitle}>Candidate Selection</Text>
            <Text style={st.hSub}>
              {candidates.length} in active selection
              {companyProfile?.company_name ? ` · ${companyProfile.company_name}` : ''}
            </Text>
          </View>
          <TouchableOpacity style={st.hBtn} onPress={onRefresh}>
            <MaterialCommunityIcons name="refresh" size={22} color="#FFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Search */}
      <View style={st.searchWrap}>
        <View style={st.searchRow}>
          <MaterialCommunityIcons name="magnify" size={20} color="#9CA3AF" />
          <TextInput
            style={st.searchInput}
            placeholder="Search by name, email, or job"
            placeholderTextColor="#9CA3AF"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery ? (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <MaterialCommunityIcons name="close-circle" size={18} color="#9CA3AF" />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>

      {/* Sub-tabs */}
      <View style={st.tabsWrap}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 12 }}>
          {TABS.map(t => {
            const count = tabCounts[t.key] || 0;
            if (t.key !== 'all' && count === 0) return null;
            const active = activeTab === t.key;
            return (
              <TouchableOpacity
                key={t.key}
                style={[st.tab, active && { backgroundColor: t.color + '15', borderColor: t.color }]}
                onPress={() => setActiveTab(t.key)}
              >
                <Text style={[st.tabLabel, active && { color: t.color, fontWeight: '700' }]}>{t.label}</Text>
                <View style={[st.tabBadge, active && { backgroundColor: t.color }]}>
                  <Text style={[st.tabBadgeTxt, active && { color: '#FFF' }]}>{count}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      {/* List */}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingVertical: 12, paddingBottom: 32 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]} />}
        showsVerticalScrollIndicator={false}
      >
        {filtered.length === 0 ? (
          <View style={st.empty}>
            <MaterialCommunityIcons name="account-check-outline" size={64} color="#D1D5DB" />
            <Text style={st.emptyTitle}>
              {candidates.length === 0 ? 'No candidates in selection' : 'No matches'}
            </Text>
            <Text style={st.emptySub}>
              {candidates.length === 0
                ? 'Shortlist candidates from the pipeline to see them here.'
                : `No candidates match "${searchQuery}".`}
            </Text>
          </View>
        ) : (
          filtered.map(c => {
            const tab = TABS.find(t => t.key === c.stage) || TABS[0];
            const initials = (c.name || 'A').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
            const canAdvance = c.stage !== 'hired' && c.stage !== 'rejected';
            return (
              <View key={c.id} style={st.card}>
                <TouchableOpacity style={st.cardBody} onPress={() => openDetails(c)} activeOpacity={0.7}>
                  <View style={[st.avatar, { backgroundColor: tab.color + '20' }]}>
                    {c.avatar
                      ? <Image source={{ uri: c.avatar }} style={st.avatarImg} />
                      : <Text style={[st.avatarTxt, { color: tab.color }]}>{initials}</Text>}
                  </View>
                  <View style={st.cardMain}>
                    <Text style={st.cardName} numberOfLines={1}>{c.name}</Text>
                    <Text style={st.cardJob} numberOfLines={1}>{c.jobTitle || c.email || '—'}</Text>
                    <View style={st.cardMetaRow}>
                      <View style={[st.stageBadge, { backgroundColor: tab.color + '15' }]}>
                        <Text style={[st.stageBadgeTxt, { color: tab.color }]}>{c.stage}</Text>
                      </View>
                      {c.interviewsScheduled > 0 && (
                        <View style={st.metaChip}>
                          <MaterialCommunityIcons name="calendar-clock" size={11} color="#6B7280" />
                          <Text style={st.metaChipTxt}>{c.interviewsScheduled}</Text>
                        </View>
                      )}
                      {c.matchScore != null && (
                        <View style={st.metaChip}>
                          <MaterialCommunityIcons name="chart-line" size={11} color="#6B7280" />
                          <Text style={st.metaChipTxt}>{Math.round(c.matchScore)}%</Text>
                        </View>
                      )}
                    </View>
                  </View>
                  <MaterialCommunityIcons name="chevron-right" size={20} color="#D1D5DB" />
                </TouchableOpacity>

                {/* Decision actions — the Selection-specific footer */}
                <View style={st.actionsRow}>
                  {canAdvance && (
                    <TouchableOpacity style={st.actionBtn} onPress={() => advance(c)}>
                      <MaterialCommunityIcons name="arrow-right-circle" size={16} color="#10B981" />
                      <Text style={[st.actionTxt, { color: '#10B981' }]}>Advance</Text>
                    </TouchableOpacity>
                  )}
                  <View style={st.actionSep} />
                  <TouchableOpacity style={st.actionBtn} onPress={() => openSchedule(c)}>
                    <MaterialCommunityIcons name="calendar-plus" size={16} color={PRIMARY} />
                    <Text style={st.actionTxt}>Interview</Text>
                  </TouchableOpacity>
                  <View style={st.actionSep} />
                  <TouchableOpacity style={st.actionBtn} onPress={() => openOffer(c)}>
                    <MaterialCommunityIcons name="file-sign" size={16} color={PRIMARY} />
                    <Text style={st.actionTxt}>Offer</Text>
                  </TouchableOpacity>
                  <View style={st.actionSep} />
                  <TouchableOpacity style={st.actionBtn} onPress={() => reject(c)}>
                    <MaterialCommunityIcons name="close-circle-outline" size={16} color="#EF4444" />
                    <Text style={[st.actionTxt, { color: '#EF4444' }]}>Reject</Text>
                  </TouchableOpacity>
                </View>
              </View>
            );
          })
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F3F4F6' },
  loading:   { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F3F4F6' },
  loadingTxt:{ fontSize: 15, color: '#6B7280', marginTop: 12 },

  header:    { paddingTop: 12, paddingBottom: 16, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn:      { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' },
  hTitle:    { fontSize: 20, fontWeight: 'bold', color: '#FFF' },
  hSub:      { fontSize: 12, color: '#D1FAE5', marginTop: 2 },

  searchWrap:{ backgroundColor: '#FFF', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  searchRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', borderRadius: 10, paddingHorizontal: 12 },
  searchInput:{ flex: 1, fontSize: 14, color: '#0F172A', paddingVertical: 10, paddingHorizontal: 8 },

  tabsWrap:  { backgroundColor: '#FFF', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  tab:       { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 6, marginRight: 8, borderRadius: 999, borderWidth: 1, borderColor: '#E5E7EB', backgroundColor: '#F9FAFB' },
  tabLabel:  { fontSize: 12, color: '#6B7280', fontWeight: '500' },
  tabBadge:  { marginLeft: 6, paddingHorizontal: 6, paddingVertical: 1, borderRadius: 10, backgroundColor: '#E5E7EB' },
  tabBadgeTxt:{ fontSize: 11, color: '#6B7280', fontWeight: '600' },

  card:      { backgroundColor: '#FFF', marginHorizontal: 16, marginBottom: 10, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB', overflow: 'hidden' },
  cardBody:  { flexDirection: 'row', alignItems: 'center', padding: 14 },
  avatar:    { width: 48, height: 48, borderRadius: 24, alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  avatarImg: { width: 48, height: 48, borderRadius: 24 },
  avatarTxt: { fontSize: 16, fontWeight: '700' },
  cardMain:  { flex: 1 },
  cardName:  { fontSize: 15, fontWeight: '600', color: '#0F172A' },
  cardJob:   { fontSize: 13, color: '#6B7280', marginTop: 2 },
  cardMetaRow:{ flexDirection: 'row', alignItems: 'center', marginTop: 6, flexWrap: 'wrap' },
  stageBadge:{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 6, marginRight: 6 },
  stageBadgeTxt:{ fontSize: 11, fontWeight: '600', textTransform: 'capitalize' },
  metaChip:  { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6, backgroundColor: '#F3F4F6', marginRight: 4 },
  metaChipTxt:{ fontSize: 11, color: '#6B7280', marginLeft: 3, fontWeight: '500' },

  actionsRow:{ flexDirection: 'row', borderTopWidth: 1, borderTopColor: '#F3F4F6', backgroundColor: '#FAFAFA' },
  actionBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 10 },
  actionTxt: { fontSize: 11, color: PRIMARY, fontWeight: '600', marginLeft: 4 },
  actionSep: { width: 1, backgroundColor: '#F3F4F6' },

  empty:     { alignItems: 'center', padding: 48 },
  emptyTitle:{ fontSize: 16, color: '#374151', fontWeight: '600', marginTop: 16 },
  emptySub:  { fontSize: 13, color: '#9CA3AF', marginTop: 6, textAlign: 'center', paddingHorizontal: 24 },
});
